import { useState, FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Mail, Lock, Loader2 } from 'lucide-react'
import { authApi, isMFAChallenge } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'
import { getErrorMessage } from '@/lib/api'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import { BRAND } from '@/config/brand'
// === Phase 2 Wave 5 Agent S1 BEGIN ===
import MFAChallengeModal from '@/components/MFA/MFAChallengeModal'
// === Phase 2 Wave 5 Agent S1 END ===

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  // PART_6_REMEMBER_ME - default false for security
  const [rememberMe, setRememberMe] = useState(false)
  // === Phase 2 Wave 5 Agent S1 BEGIN ===
  const [mfaChallenge, setMfaChallenge] = useState<{
    challenge_token: string
    method: 'totp' | 'sms'
  } | null>(null)
  // === Phase 2 Wave 5 Agent S1 END ===

  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const setAuth = useAuthStore((state) => state.setAuth)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const data = await authApi.login({ email, password, remember_me: rememberMe })
      // === Phase 2 Wave 5 Agent S1 BEGIN ===
      if (isMFAChallenge(data)) {
        setMfaChallenge({
          challenge_token: data.challenge_token,
          method: data.method,
        })
        return
      }
      // === Phase 2 Wave 5 Agent S1 END ===
      setAuth(data.user, data.access_token, data.refresh_token ?? null)
      toast.success(t('auth.welcomeBack'))
      navigate('/dashboard')
    } catch (error) {
      toast.error(getErrorMessage(error))
    } finally {
      setIsLoading(false)
    }
  }

  // === Phase 2 Wave 5 Agent S1 BEGIN ===
  const handleMFASuccess = (tok: {
    access_token: string
    refresh_token?: string | null
    user: any
  }) => {
    setAuth(tok.user, tok.access_token, tok.refresh_token ?? null)
    setMfaChallenge(null)
    toast.success(t('auth.welcomeBack'))
    navigate('/dashboard')
  }
  // === Phase 2 Wave 5 Agent S1 END ===

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-700/10 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-32 h-32 rounded-2xl shadow-2xl mb-4 bg-gradient-to-br ${BRAND.gradient}`}>
            <span className="text-white text-6xl font-bold">{BRAND.initial}</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">{BRAND.name}</h1>
          <p className="text-gray-400">{BRAND.tagline}</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('auth.welcomeBack')}
            </h2>
            <p className="text-gray-600">{t('auth.signIn')}</p>
          </div>

          {/* WAVE-12-6-E2E-RED-SINCE-2026-10-17-01: each <label> is bound to its
              input (htmlFor/id). Unbound, the inputs' accessible names fell back
              to their placeholders, so a screen reader announced
              "email@example.com" instead of "Email" and getByLabel() could never
              find the field -- the E2E login timed out on every run. */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="login-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  dir="ltr"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  dir="ltr"
                />
              </div>
              {/* Remember Me + Forgot Password */}
              <div className="mt-2 flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                  />
                  <span>{t('auth.rememberMe', isArabic ? 'تذكرني' : 'Remember me')}</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {t('auth.forgotPassword')}
                </Link>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('auth.loggingIn')}
                </>
              ) : (
                t('auth.loginButton')
              )}
            </button>
          </form>

          {/* Sign up link */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-gray-600 text-sm">
              {t('auth.noAccount', "Don't have an account?")}{' '}
              <Link
                to="/signup"
                className="text-blue-600 hover:text-blue-700 hover:underline font-semibold"
              >
                {t('auth.signUp', 'Sign up')}
              </Link>
            </p>
          </div>

          {/* Language Switcher */}
          <div className="mt-4 flex justify-center">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          {BRAND.name} © {BRAND.copyrightYear} - {isArabic ? BRAND.copyrightSuffixAr : BRAND.copyrightSuffix}
        </p>
      </div>

      {/* === Phase 2 Wave 5 Agent S1 BEGIN === */}
      {mfaChallenge && (
        <MFAChallengeModal
          challengeToken={mfaChallenge.challenge_token}
          method={mfaChallenge.method}
          onSuccess={handleMFASuccess}
          onCancel={() => setMfaChallenge(null)}
        />
      )}
      {/* === Phase 2 Wave 5 Agent S1 END === */}
    </div>
  )
}